import React, { Fragment, useCallback, useRef, useEffect, useState } from 'react';
import { 
  Wand2, 
  Check, 
  ChevronDown, 
  Sparkles, 
  Code, 
  BookOpen,
  CheckCircle,
} from 'lucide-react';
import { PlaybookTransformItem, PlaybookExpression, PlaybookEventConfig } from '@/types/playbook';
import { EventSchema, UnifiedSchema, Parameter, DataType } from '@/types/schema';
import ReactDOM from 'react-dom'; // Added ReactDOM
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { LuaTemplate } from '@/data/luaTemplates';
import PropertyPathAutocomplete from '@components/common/PropertyPathAutocomplete';
import clsx from 'clsx';
import LuaCodeEditor from '../LuaCodeEditor';
import {
  TypeIconString,
  TypeIconNumber,
  TypeIconBoolean,
  TypeIconObject,
  TypeIconArray,
  TypeIconNull,
} from '@components/common/DataTypeIcons';
import { getTypeColorClass } from '@utils/schemaUtils';
import { LUA_TEMPLATES, getLuaTemplateIcon } from '@/data/luaTemplates';
import { useNotifications } from '@/contexts/NotificationContext';
import { useUIContext } from '@/providers/UIProvider';
import { usePortalRoot } from '@contexts/PortalRootContext'; // Added usePortalRoot
import { useTooltipPosition } from '@hooks/useTooltipPosition'; // Added useTooltipPosition
import axios from 'axios';

const DataTypeIcon: React.FC<{ type: DataType | 'any'; className?: string }> = ({ type, className = "h-4 w-4" }) => {
  switch (type) {
    case 'string': return <TypeIconString className={className} />;
    case 'number': return <TypeIconNumber className={className} />;
    case 'boolean': return <TypeIconBoolean className={className} />;
    case 'object': return <TypeIconObject className={className} />;
    case 'array': return <TypeIconArray className={className} />;
    case 'null': return <TypeIconNull className={className} />;
    default: return null;
  }
};

interface ExpressionEditorProps {
  transform: PlaybookTransformItem;
  onUpdate: (updates: Partial<PlaybookTransformItem>) => void;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[]; // New prop
  onCloseEditor: () => void;
  outputSchema?: UnifiedSchema | null;
  inputSchema?: any; // For AI context
}

type ExpressionEditorMode = 'ai' | 'advanced';

interface ExpressionEditorState {
  mode: ExpressionEditorMode;
  aiPrompt: string;
  selectedTemplate: LuaTemplate | null;
  isGenerating: boolean;
  luaContent: string;
  generatedExplanation?: string;
  suggestedOutputProperties?: { path: string; dataType: string }[];
}

const ExpressionEditor: React.FC<ExpressionEditorProps> = ({
  transform,
  onUpdate,
  allEvents,
  uiEventConfigs,
  onCloseEditor,
  outputSchema = null,
  inputSchema,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0); // New state for key
  const content = transform.content as PlaybookExpression;
  const editorRef = useRef<HTMLDivElement>(null);
  const listboxButtonRef = useRef<HTMLButtonElement>(null); // Ref for the ListboxButton
  const listboxOptionsPortalRef = useRef<HTMLDivElement>(null); // New ref for the ListboxOptionsPortal
  const { showNotification } = useNotifications();
  const { openDocumentationPanel } = useUIContext();
  const { portalRoot } = usePortalRoot(); // Get portalRoot from context

  const [editorState, setEditorState] = useState<ExpressionEditorState>({
    mode: 'ai',
    aiPrompt: '',
    selectedTemplate: null,
    isGenerating: false,
    luaContent: content.body || '',
  });

  const detectDataType = (propertyPath: string, properties: Parameter[]): DataType | 'any' => {
    const property = properties.find(p => p.fullPath === propertyPath || p.path === propertyPath);
    if (property && property.dataTypes && property.dataTypes.length > 0) {
      return property.dataTypes[0];
    }
    return 'any';
  };

  const updateContent = (updates: Partial<PlaybookExpression>) => {
    onUpdate({
      content: { ...content, ...updates }
    });
  };

  const handleModeChange = (newMode: ExpressionEditorMode) => {
    setEditorState(prev => ({ ...prev, mode: newMode }));
  };

  const handleTemplateSelect = (template: LuaTemplate) => {
    setEditorState(prev => ({ 
      ...prev, 
      selectedTemplate: template,
      aiPrompt: template.prompt || `Generate Lua code for: ${template.description}`,
      luaContent: template.codeTemplate || prev.luaContent // Pre-fill with template code if available
    }));
    if (template.codeTemplate) {
      updateContent({ body: template.codeTemplate });
    }
  };

  const handleOpenDocs = () => {
    openDocumentationPanel('https://docs.metarouter.io/docs/custom-expressions');
  };

  const handleGenerateLua = async () => {
    if (!editorState.aiPrompt.trim()) return;

    setEditorState(prev => ({ ...prev, isGenerating: true }));

    try {
      // Prepare context for AI
      const inputSchemaProperties = inputSchema ? 
        Object.keys(inputSchema.properties || {}).map(key => ({
          path: key,
          type: inputSchema.properties[key].type || 'string'
        })) : [];

      const availableEvents = allEvents.map(event => event.name);
      const availableProperties = inputSchemaProperties.map(p => p.path);

      const response = await axios.post('/api/ai/generateContent', {
        contentType: 'luaExpressionGeneration',
        context: {
          prompt: editorState.aiPrompt,
          availableEvents,
          availableProperties,
          inputSchemaProperties,
          templates: LUA_TEMPLATES
        }
      });

      if (response.data.luaExpressionGenerationResponse) {
        const { luaExpression, explanation, suggestedTemplate, suggestedOutputProperties } = response.data.luaExpressionGenerationResponse;
        
        setEditorState(prev => ({ 
          ...prev, 
          luaContent: luaExpression,
          generatedExplanation: explanation,
          selectedTemplate: suggestedTemplate ? LUA_TEMPLATES.find(t => t.id === suggestedTemplate) || null : null,
          suggestedOutputProperties: suggestedOutputProperties || []
        }));

        // Update the transform content
        updateContent({ body: luaExpression });
      }
    } catch (error: any) {
      console.error('Failed to generate Lua expression:', error);
      
      let errorMessage = 'Failed to generate Lua expression. Please try again.';
      
      if (error.response?.status === 503) {
        errorMessage = 'AI service is currently unavailable. Please try again later.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid request. Please check your input and try again.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification('error', errorMessage, 5000);
    } finally {
      setEditorState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleLuaChange = (newLua: string) => {
    setEditorState(prev => ({ ...prev, luaContent: newLua }));
    updateContent({ body: newLua });
  };

  const removeOutputProperty = (index: number) => {
    const updatedProperties = [...(content.declaredOutputProperties || [])];
    updatedProperties.splice(index, 1);
    updateContent({ declaredOutputProperties: updatedProperties });
  };


  const handleEventSelectChange = useCallback((selectedEventIds: string[]) => {
    onUpdate({ 
      eventIds: selectedEventIds,
      // Preserve existing content and type information
      content: transform.content,
      inputType: transform.inputType,
      outputType: transform.outputType
    });
  }, [onUpdate, transform.content, transform.inputType, transform.outputType]);

  const currentEventAssociations = uiEventConfigs.filter(event =>
    (transform.eventIds || []).includes(event.id)
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseEditor();
    }
  }, [onCloseEditor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click target is inside the editorRef
      if (editorRef.current && editorRef.current.contains(event.target as Node)) {
        return; // Click is inside the editor, do nothing
      }

      // Check if the click target is inside the PropertyPathAutocomplete dropdown
      // The dropdown has a class 'dropdown-options' as seen in PropertyPathAutocomplete.tsx
      if ((event.target as HTMLElement).closest('.dropdown-options')) {
        return; // Click is inside the autocomplete dropdown, do nothing
      }

      // Check if the click target is inside the ListboxButton
      if (listboxButtonRef.current && listboxButtonRef.current.contains(event.target as Node)) {
        return; // Click is inside the ListboxButton, do nothing
      }

      // Check if the click target is inside a ListboxOption (using the data attribute)
      if ((event.target as HTMLElement).closest('[data-listbox-option="true"]')) {
        return; // Click is inside a ListboxOption, do nothing
      }

      // If the click is outside all controlled elements, close the editor
      onCloseEditor();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCloseEditor]);

  return (
    <div ref={editorRef} className="flex flex-col gap-4 py-4 px-4" onKeyDown={handleKeyDown}>

      {/* Quick Templates and Mode Toggle */}
      <div className="flex items-start gap-4">
        {/* Quick Templates */}
        <div className="flex-1 flex flex-col min-h-[66px]">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Quick Templates</label>
          <Listbox value={editorState.selectedTemplate} onChange={handleTemplateSelect}>
            {({ open }) => (
              <div className="relative">
                <ListboxButton 
                  ref={listboxButtonRef} // Added ref
                  className="relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <div className="flex items-center gap-2">
                    {editorState.selectedTemplate ? (
                      <>
                        {React.createElement(getLuaTemplateIcon(editorState.selectedTemplate.id), { 
                          className: "h-4 w-4 text-gray-500 dark:text-gray-400" 
                        })}
                        <span className="block truncate">{editorState.selectedTemplate.name}</span>
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select a template...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                {portalRoot && (
                  <ListboxOptionsPortal
                    triggerRef={listboxButtonRef}
                    portalRoot={portalRoot}
                    isOpen={open}
                    forwardedRef={listboxOptionsPortalRef}
                  >
                    {LUA_TEMPLATES.map((template) => {
                      const IconComponent = getLuaTemplateIcon(template.id);
                      return (
                        <ListboxOption
                          key={template.id}
                          className={({ active }) =>
                            clsx(
                              active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                              'relative cursor-pointer select-none py-2 pl-3 pr-9'
                            )
                          }
                          value={template}
                          data-listbox-option="true"
                        >
                          {({ selected, active }) => (
                            <>
                              <div className="flex items-center gap-2">
                                <IconComponent className={clsx(
                                  'h-4 w-4',
                                  active ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                                )} />
                                <div className="flex flex-col">
                                  <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                    {template.name}
                                  </span>
                                  <span className={clsx(
                                    'text-xs truncate',
                                    active ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                                  )}>
                                    {template.description}
                                  </span>
                                </div>
                              </div>
                              {selected ? (
                                <span
                                  className={clsx(
                                    active ? 'text-white' : 'text-blue-600',
                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                  )}
                                >
                                  <Check className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      );
                    })}
                  </ListboxOptionsPortal>
                )}
              </div>
            )}
          </Listbox>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 justify-center min-h-[32px] mt-5">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              editorState.mode === 'ai' 
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                : 'text-gray-600 dark:text-gray-400'
            }`}
            onClick={() => handleModeChange('ai')}
          >
            <Sparkles className="h-4 w-4" />
            Generate
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              editorState.mode === 'advanced' 
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                : 'text-gray-600 dark:text-gray-400'
            }`}
            onClick={() => handleModeChange('advanced')}
          >
            <Code className="h-4 w-4" />
            Code
          </button>
        </div>
      </div>

      {/* AI Mode Interface */}
      {editorState.mode === 'ai' && (
        <div className="space-y-4">
          {/* AI Prompt Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Describe your Lua Expression</label>
            <div className="relative">
              <textarea
                value={editorState.aiPrompt}
                onChange={(e) => setEditorState(prev => ({ ...prev, aiPrompt: e.target.value }))}
                placeholder="e.g., 'Extract user ID and event name into a new object' or 'Calculate a new property based on two existing properties'"
                className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white resize-none"
                rows={3}
              />
              <button
                onClick={handleGenerateLua}
                disabled={!editorState.aiPrompt.trim() || editorState.isGenerating}
                className="absolute bottom-3 right-1.5 px-4 py-1 bg-blue-600 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                {editorState.isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Generated Lua Preview */}
          {editorState.luaContent && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-gray-100">Generated Lua Expression</h4>
                <button
                  onClick={() => handleModeChange('advanced')}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Edit Manually
                </button>
              </div>
              <div className="h-48 border border-gray-200 dark:border-gray-700 rounded">
                <LuaCodeEditor
                  value={editorState.luaContent}
                  showLineNumbers={true}
                  rows={12}
                  disabled={true}
                  className="h-full"
                />
              </div>
              {editorState.generatedExplanation && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <strong>What this expression does:</strong> {editorState.generatedExplanation}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Advanced Mode Interface */}
      {editorState.mode === 'advanced' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Lua Expression <span className="text-red-500">*</span>
            </label>
            <LuaCodeEditor
              value={editorState.luaContent}
              onChange={handleLuaChange}
              rows={12}
              showLineNumbers={true}
              placeholder={`-- Example Lua expression
local result = {}

-- Access input data
local user_name = input.user.name
local user_age = input.user.age

-- Perform calculations
result.full_name = user_name .. " (Age: " .. user_age .. ")"
result.is_adult = user_age >= 18
result.category = user_age < 18 and "minor" or "adult"

-- Return the result
return result`}
            />
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Use <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">input</code> to access input data. 
              Return an object or value to set output properties.
            </div>
          </div>
          {editorState.suggestedOutputProperties && editorState.suggestedOutputProperties.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium dark:text-gray-100 mb-2">Suggested Output Properties</h4>
              <div className="flex flex-wrap gap-2">
                {editorState.suggestedOutputProperties.map((prop, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      const newProperty = { path: prop.path, dataType: prop.dataType as DataType };
                      updateContent({
                        declaredOutputProperties: [...(content.declaredOutputProperties || []), newProperty]
                      });
                      setEditorState(prev => ({
                        ...prev,
                        suggestedOutputProperties: prev.suggestedOutputProperties?.filter((_, i) => i !== index)
                      }));
                    }}
                    className={clsx(
                      "inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium transition-colors",
                      getTypeColorClass(prop.dataType as DataType),
                      "hover:opacity-80"
                    )}
                  >
                    <DataTypeIcon type={prop.dataType as DataType} className="h-4 w-4 mr-1.5" />
                    <span>{prop.path}</span>
                    <CheckCircle className="h-4 w-4 ml-1.5" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Declared Output Properties */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Declared Output Properties
        </label>
        <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus-within:ring-blue-500 focus-within:border-blue-500 dark:bg-gray-800 flex flex-wrap items-center gap-2">
          {(content.declaredOutputProperties || []).map((property, index) => (
            <div
              key={index}
              className={clsx(
                "inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium",
                getTypeColorClass(property.dataType as DataType)
              )}
            >
              <DataTypeIcon type={property.dataType as DataType} className="h-4 w-4 mr-1.5" />
              <span>{property.path}</span>
              <button
                onClick={() => removeOutputProperty(index)}
                className="ml-2 -mr-1 h-5 w-5 flex-shrink-0 rounded-full inline-flex items-center justify-center text-gray-600 hover:bg-black/20"
              >
                <span className="sr-only">Remove {property.path}</span>
                <svg className="h-2.5 w-2.5" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                  <path strokeLinecap="round" strokeWidth="1" d="M1 1l6 6m0-6L1 7" />
                </svg>
              </button>
            </div>
          ))}
          <div className="flex-grow">
            <PropertyPathAutocomplete
              key={autocompleteKey} // Use key to force remount and clear input
              label=""
              value={inputValue}
              onSelect={(fullPath) => {
                if (fullPath) {
                  const dataType = detectDataType(fullPath, outputSchema?.parameters || []);
                  const newProperty = { path: fullPath, dataType };
                  updateContent({
                    declaredOutputProperties: [...(content.declaredOutputProperties || []), newProperty]
                  });
                  setInputValue(''); // Clear inputValue state
                  setAutocompleteKey(prevKey => prevKey + 1); // Increment key to force remount
                }
              }}
              availableProperties={outputSchema?.parameters || []}
              usedProperties={content.declaredOutputProperties?.map(p => p.path) || []}
              expectedDataType="any"
              placeholder="Add property..."
              className="!mt-0"
              unstyled
            />
          </div>
        </div>
      </div>

      {/* Assigned Events */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Assigned Events
        </label>
        <div className="flex flex-row gap-2 items-end w-full">
          <Listbox
            value={currentEventAssociations}
            onChange={(selectedEvents: PlaybookEventConfig[]) => handleEventSelectChange(selectedEvents.map(event => event.id))}
            multiple
          >
            {({ open }) => (
              <div className="relative flex-1">
                <ListboxButton
                  className={clsx(
                    "relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm",
                    "border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20",
                    "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  )}
                >
                  <div className="flex flex-wrap gap-1 pr-8">
                    {currentEventAssociations.length > 0 ? (
                      currentEventAssociations.map(event => (
                        <span
                          key={event.id}
                          className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {event.name}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault(); // Add preventDefault to stop default ListboxButton behavior
                              handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                              }
                            }}
                            className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                          >
                            <span className="sr-only">Remove {event.name}</span>
                            <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </span>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                {portalRoot && (
                  <ListboxOptionsPortal
                    triggerRef={listboxButtonRef}
                    portalRoot={portalRoot}
                    isOpen={open}
                    forwardedRef={listboxOptionsPortalRef}
                  >
                    {uiEventConfigs.map((event) => (
                      <ListboxOption
                        key={event.id}
                        className={({ active }) =>
                          clsx(
                            active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                            'relative cursor-pointer select-none py-2 pl-10 pr-4' // Changed cursor-default to cursor-pointer
                          )
                        }
                        value={event}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                              {event.name}
                            </span>
                            {selected ? (
                              <span
                                className={clsx(
                                  active ? 'text-white' : 'text-blue-600',
                                  'absolute inset-y-0 left-0 flex items-center pl-3'
                                )}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptionsPortal>
                )}
              </div>
            )}
          </Listbox>
          <button
            type="button"
            onClick={() => {
              handleEventSelectChange(uiEventConfigs.map(event => event.id));
            }}
            disabled={(transform.eventIds || []).length === uiEventConfigs.length}
            className={clsx(
              "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
              "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600",
              (transform.eventIds || []).length === uiEventConfigs.length ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            Assign All
          </button>
        </div>
      </div>

      {/* Documentation Link */}
      <div className="flex">
        <button
          onClick={handleOpenDocs}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
        >
          <BookOpen className="h-4 w-4" />
          Documentation
        </button>
      </div>

    </div>
  );
};

export default ExpressionEditor;

interface ListboxOptionsPortalProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  portalRoot: HTMLElement;
  isOpen: boolean;
  forwardedRef: React.RefObject<HTMLDivElement>; 
}

const ListboxOptionsPortal: React.FC<ListboxOptionsPortalProps> = ({
  children,
  triggerRef,
  portalRoot,
  isOpen,
  forwardedRef,
}) => {
  const { tooltipStyle, tooltipRef } = useTooltipPosition(triggerRef, {
    side: 'bottom',
    align: 'start',
    sideOffset: 2,
    portalRoot,
    isOpen,
  });

  useEffect(() => {
    if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = tooltipRef.current;
    }
  }, [forwardedRef, tooltipRef]);

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      style={{ ...tooltipStyle, width: tooltipStyle.width || 'auto' }}
      className="pointer-events-auto z-50"
    >
      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ListboxOptions
          static
          className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
            dark:bg-gray-800 dark:border dark:border-gray-700"
        >
          {children}
        </ListboxOptions>
      </Transition>
    </div>,
    portalRoot
  );
};
